import type {
  CartAddResponse,
  CartMutateResponse,
  NormalizedCart,
  Generation,
  ProductType,
  SessionInfo,
  SessionQuota,
  Style,
  TemplateMockup,
  UploadResult,
} from "./types";

// API base URL.
//   - Dev: defaults to "/api" — Vite proxies /api/* → :8787 (same-origin).
//   - Embedded in Shopify theme: build with VITE_API_URL=https://your-api.example.com
//     so the storefront-hosted bundle calls the deployed api directly.
// `credentials: "include"` makes the session cookie travel cross-origin in
// production. Same-origin (dev) ignores it.
const BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");
/** R2 public CDN base (Cloudflare). When set, asset paths go directly to
 *  R2 instead of through the api host — cheaper, faster, zero-egress. */
const R2_BASE = (import.meta.env.VITE_R2_PUBLIC_URL ?? "").replace(/\/$/, "");

/**
 * Resolve a relative asset path (e.g. "/styles/foo.png", "/mockups/bar.png",
 * "/composites/<hash>.png") into an absolute URL.
 *
 * Strategy:
 *   1. Absolute URLs (Fal CDN, R2 CDN, https://...) → pass-through.
 *   2. R2-backed asset categories (/styles, /mockups, /composites,
 *      /uploads) → prefix with VITE_R2_PUBLIC_URL when set.
 *   3. Everything else → prefix with VITE_API_URL (or "/api" Vite-proxy
 *      default in dev).
 */
export function assetUrl(path: string | undefined | null): string {
  if (!path) return "";
  if (/^(https?:)?\/\//.test(path)) return path;
  if (!path.startsWith("/")) return path;
  if (R2_BASE && /^\/(styles|mockups|composites|uploads)\//.test(path)) {
    return `${R2_BASE}${path}`;
  }
  return `${BASE}${path}`;
}

async function req(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, { credentials: "include", ...init });
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`api ${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function fetchStyles(): Promise<Style[]> {
  // Add cache buster so browser doesn't serve old styles from cache
  const res = await req(`/styles?t=${Date.now()}`);
  const data = await json<{ styles: Style[] }>(res);
  return data.styles;
}

/** GET /products/prices → live Shopify price per (productType, size index).
 *  A `null` entry (or a missing type) means "no live price" → the caller keeps
 *  its bundled fallback from data/products.ts. Never throws. */
export async function fetchLivePrices(): Promise<Record<string, (number | null)[]>> {
  try {
    const res = await req(`/products/prices`);
    const data = await json<{ prices: Record<string, (number | null)[]> }>(res);
    return data.prices ?? {};
  } catch {
    return {};
  }
}

/** GET /templates/:styleId/mockups → pre-rendered template-on-wall composites
 *  for the template detail page. First fetch per template renders + caches in
 *  R2 (~10-20s); subsequent calls return instantly. */
export async function fetchTemplateMockups(
  styleId: string,
  count = 4,
): Promise<TemplateMockup[]> {
  const res = await req(
    `/templates/${encodeURIComponent(styleId)}/mockups?count=${count}`,
  );
  const data = await json<{ mockups: TemplateMockup[] }>(res);
  return data.mockups ?? [];
}

export async function uploadImage(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("image", file);
  const res = await req("/upload", { method: "POST", body: fd });
  return json<UploadResult>(res);
}

export type StartGenerationResult =
  | { kind: "ok"; generation: Generation; cached: boolean }
  | { kind: "quota_exhausted"; quota: SessionQuota };

export async function startGeneration(input: {
  upload_id: string;
  style_id: string;
  dad_name: string;
}): Promise<StartGenerationResult> {
  const res = await req("/generate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (res.status === 402) {
    const data = (await res.json()) as { quota: SessionQuota };
    return { kind: "quota_exhausted", quota: data.quota };
  }
  const data = await json<{ generation: Generation; cached: boolean }>(res);
  return { kind: "ok", generation: data.generation, cached: data.cached };
}

export async function fetchSession(): Promise<SessionInfo> {
  const res = await req("/session");
  return json<SessionInfo>(res);
}

export async function submitEmail(input: {
  email: string;
  dad_name?: string;
}): Promise<SessionInfo> {
  const res = await req("/session/email", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<SessionInfo>(res);
}

export interface SignatureInput {
  text: string;
  font_id: string; // one of SIGNATURE_FONTS.id values
  scale?: number;  // size multiplier, 1.0 = auto (matches the preview slider)
}

export async function addToCart(input: {
  generation_id: string;
  product_type: ProductType;
  /** Existing cart to append to. Omit on the first add. */
  cart_id?: string;
  quantity?: number;
  /** Customer's chosen size — index into products.json[product_type].sizes. */
  size_index?: number;
  /** Frame color label (only relevant for framed_canvas). */
  frame_color?: string;
  signature?: SignatureInput;
  /** Widget mode (mounted on PDP): the PDP's variant id, lets cart skip
   *  dynamic-product creation and add the Shopify product directly. */
  variant_id?: string;
}): Promise<CartAddResponse> {
  const res = await req("/cart", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<CartAddResponse>(res);
}

export async function updateCartLine(input: {
  cart_id: string;
  line_id: string;
  quantity: number;
}): Promise<CartMutateResponse> {
  const res = await req("/cart/lines/update", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<CartMutateResponse>(res);
}

export async function removeCartLine(input: {
  cart_id: string;
  line_id: string;
}): Promise<CartMutateResponse> {
  const res = await req("/cart/lines/remove", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<CartMutateResponse>(res);
}

/**
 * Replace the cart's discount codes. Pass `codes: []` to clear all.
 * Server returns the full cart; if a code was invalid, it appears in
 * `cart.discountCodes` with `applicable: false` — caller should detect
 * and surface an error instead of treating it as applied.
 */
export async function applyCartDiscount(input: {
  cart_id: string;
  codes: string[];
}): Promise<CartMutateResponse> {
  const res = await req("/cart/discount", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return json<CartMutateResponse>(res);
}

/** Restore a persisted cart by id. Returns null when the cart is gone
 *  (expired / completed) so the caller can drop its localStorage copy. */
export async function getCart(cartId: string): Promise<NormalizedCart | null> {
  const res = await req(`/cart/${encodeURIComponent(cartId)}`);
  if (res.status === 404) return null;
  const data = await json<CartMutateResponse>(res);
  return data.cart;
}

export async function pollGeneration(id: string): Promise<Generation> {
  const res = await req(`/generation/${id}`);
  const data = await json<{ generation: Generation }>(res);
  return data.generation;
}

/**
 * Polls the generation until status leaves the "in flight" set or the
 * timeout elapses. Throws on failure or timeout, with the LAST observed
 * status appended to the timeout message so we can tell whether the
 * backend was still working (running) vs. never picked up (queued) vs.
 * something else.
 */
export async function waitForPreview(
  id: string,
  opts: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<Generation> {
  const intervalMs = opts.intervalMs ?? 600;
  const timeoutMs = opts.timeoutMs ?? 30_000;
  const deadline = Date.now() + timeoutMs;
  let lastStatus = "unknown";
  let polls = 0;

  while (Date.now() < deadline) {
    const g = await pollGeneration(id);
    lastStatus = g.status;
    polls++;
    if (g.status === "ready" || g.status === "final") return g;
    if (g.status === "failed") {
      // Surface the real server-side reason (e.g. fal_timeout_after_150000ms,
      // fal_returned_no_image, or a Fal API error) so failures are
      // self-diagnosing instead of forcing a Fly-logs lookup.
      throw new Error(
        g.error_message
          ? `generation failed: ${g.error_message}`
          : "generation failed (no error detail from server)",
      );
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error(
    `generation timed out after ${Math.round(timeoutMs / 1000)}s (last status: ${lastStatus}, polls: ${polls}, id: ${id})`,
  );
}

/** Scene config from admin panel */
export interface MockupScene {
  id: string;
  label: string;
  type: "product_flat" | "room_scene";
  bgImage?: string;
  artPosition?: {
    x: number; y: number; w: number; h: number;
    corners?: { tl: [number, number]; tr: [number, number]; bl: [number, number]; br: [number, number] };
  };
  shadow?: boolean;
  frameStyle?: string;
  enabled?: boolean;
}

export type MockupScenesMap = Record<string, MockupScene[]>;

/** Fetch admin-configured mockup scenes (public endpoint, no auth needed) */
export async function fetchMockupScenes(): Promise<MockupScenesMap> {
  const res = await req("/mockup-scenes");
  const data = await json<{ scenes: MockupScenesMap }>(res);
  return data.scenes;
}
